import { Box, Stack, Typography } from "@mui/joy";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { api } from "../../../../../interceptors/api";
import { TranscribedAdditionalUserStoryType } from "../../../../../types/TranscribedAdditionalUserStoryType";
import { ClassicyButton } from "../../components/button/ClassicyButton";
import { ClassicyControlGroup } from "../../components/control-group/ClassicyControlGroup";
import "../styles/QuestionnaireSteps.css";

interface AdditionalTextStepProps {
  onChange: (text: string, language: string, audioUrl: string) => void;
  onRecordingChange?: (isRecording: boolean) => void;
  exhibitionId: number;
}

function AdditionalTextStep({
  onChange,
  onRecordingChange,
  exhibitionId,
}: AdditionalTextStepProps) {
  const [audioUrl, setAudioUrl] = useState("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcribedText, setTranscribedText] = useState("");
  const [transcribedTextEN, setTranscribedTextEN] = useState("");
  const [originalLanguage, setOriginalLanguage] = useState("de");

  const [transcribedAudioUrl, setTranscribedAudioUrl] = useState("");
  const [showTranscription, setShowTranscription] = useState(false);
  const [transcriptionAccepted, setTranscriptionAccepted] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<any[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        const sizeInMB = audioBlob.size / (1024 * 1024);
        if (sizeInMB > 4.5) {
          toast.error(
            "Audiodatei zu groß (max 4.5 MB). Bitte kürzer aufnehmen.",
          );
          return;
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(audioUrl);
        setAudioBlob(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      onRecordingChange?.(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Fehler beim Zugriff auf das Mikrofon");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      onRecordingChange?.(false);
      audioChunksRef.current = [];

      // Stop all tracks to release microphone
      const tracks = mediaRecorderRef.current.stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;

    setIsTranscribing(true);
    const formData = new FormData();
    formData.append("audio", audioBlob, "recording.webm");

    try {
      const res = await api.post<TranscribedAdditionalUserStoryType>(
        `/api/user-story/transcribe-audio/exhibition/${exhibitionId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.data.transcribedTextDE || res.data.transcribedTextEN) {
        if (res.data.originalLanguage === "de") {
          setTranscribedText(res.data.transcribedTextDE);
          setTranscribedTextEN(res.data.transcribedTextEN);
          setOriginalLanguage("de");
        } else {
          setTranscribedText(res.data.transcribedTextEN);
          setTranscribedTextEN(res.data.transcribedTextEN);
          setOriginalLanguage("en");
        }
        setTranscribedAudioUrl(res.data.audioUrl);
        setShowTranscription(true);
      } else {
        toast.error("Transkription fehlgeschlagen");
      }
    } catch (error) {
      console.error("Error transcribing audio:", error);
      toast.error("Fehler bei der Transkription");
    } finally {
      setIsTranscribing(false);
    }
  };

  const acceptTranscription = () => {
    onChange(transcribedTextEN, originalLanguage, transcribedAudioUrl);
    setTranscriptionAccepted(true);
  };

  const discardAndRecordNew = () => {
    setAudioUrl("");
    setAudioBlob(null);
    setTranscribedText("");
    setTranscribedAudioUrl("");
    setShowTranscription(false);
    audioChunksRef.current = [];
  };

  return (
    <Box className="questionnaire-step">
      <Typography level="h2" sx={{ mb: 3, textAlign: "center" }}>
        Möchtest du noch etwas hinzufügen? (Optional)
      </Typography>
      <Typography level="h3" sx={{ mb: 3, textAlign: "center", opacity: 0.8 }}>
        Nimm eine Geschichte auf oder überspringe diesen Schritt
      </Typography>

      <Stack spacing={3} alignItems="center">
        {/* Recording Controls */}
        {!showTranscription && (
          <ClassicyControlGroup
            label="Audioaufnahme (Optional)"
            columns={false}
          >
            <Stack spacing={2} alignItems="center">
              {isRecording ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <ClassicyButton
                    buttonType="button"
                    onClickFunc={stopRecording}
                  >
                    Aufnahme stoppen
                  </ClassicyButton>
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#CC0000",
                      animation: "pulse 1.5s infinite",
                    }}
                  />
                </Stack>
              ) : (
                <ClassicyButton
                  buttonType="button"
                  onClickFunc={startRecording}
                  disabled={!!audioUrl}
                >
                  Aufnahme starten
                </ClassicyButton>
              )}

              {/* Audio Preview */}
              {audioUrl && !isRecording && (
                <Box sx={{ width: "100%", maxWidth: 500 }}>
                  <Typography level="h3" sx={{ mb: 1 }}>
                    Aufnahme-Vorschau
                  </Typography>
                  <audio controls src={audioUrl} style={{ width: "100%" }} />

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ mt: 2 }}
                    justifyContent="center"
                  >
                    <ClassicyButton
                      buttonType="button"
                      onClickFunc={transcribeAudio}
                      disabled={isTranscribing}
                    >
                      {isTranscribing ? "Transkribiere..." : "Transkribieren"}
                    </ClassicyButton>
                    <ClassicyButton
                      buttonType="reset"
                      onClickFunc={discardAndRecordNew}
                      disabled={isTranscribing}
                    >
                      Neu aufnehmen
                    </ClassicyButton>
                  </Stack>
                </Box>
              )}
            </Stack>
          </ClassicyControlGroup>
        )}

        {/* Transcription Result */}
        {showTranscription && (
          <ClassicyControlGroup label="Transkribierter Text" columns={false}>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  p: 2,
                  border: "2px solid",
                  borderColor: "neutral.300",
                  borderRadius: "md",
                  backgroundColor: "background.level1",
                  mb: 2,
                }}
                className="classicyControlGroupTranscribedTextContainer"
              >
                <Typography
                  level="body-md"
                  className="classicyControlGroupTranscribedText"
                >
                  {transcribedText}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2} justifyContent="center">
                <ClassicyButton
                  isDefault
                  onClickFunc={acceptTranscription}
                  disabled={transcriptionAccepted}
                >
                  Akzeptieren
                </ClassicyButton>
                <ClassicyButton onClickFunc={discardAndRecordNew}>
                  Neu aufnehmen
                </ClassicyButton>
              </Stack>
            </Box>
          </ClassicyControlGroup>
        )}
      </Stack>
    </Box>
  );
}

export default AdditionalTextStep;
